import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"

import { Button, Card, Drawer, Form, Input, Select } from "antd"
import { CheckboxChangeEvent } from "antd/es/checkbox"
import Checkbox from "antd/lib/checkbox"

import { NumericInput } from "@/components/_Shared/NumericInput"

import { useCustomerClientsSelectListQuery } from "@/hook/CustomerClients/useCustomerClientQuery"
import { useCustomersSelectListQuery } from "@/hook/Dictionaries/Customers/useCustomerQuery"
import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useEmployeeMutation } from "@/hook/Dictionaries/useEmployeeMutation"
import { useEmployeeByIdQuery, useEmployeeRolesQuery } from "@/hook/Dictionaries/useEmployeeQuery"
import { useRolesWithAttributesQuery } from "@/hook/Dictionaries/useRoleQuery"

import { ICustomer, ICustomerSelectListItem } from "@/types/ICustomer"
import { attrKeyForCustomerClients, attrKeyForCustomers, attrKeyForDepots } from "@/types/IRole"
import { USER_PASSWORD_MINLENGTH, USER_PASSWORD_REGEXP } from "@/types/IUser"

import { PHONE_LENGTH } from "@/utils/constants"
import { filterSelectOption } from "@/utils/filterSelectOption"

export interface EmployeeAddFormProps {
  open(id?: string, roleName?: string, customerId?: number): void
  close(): void
}

interface EmployeeAddFormValues {
  firstName: string
  lastName: string
  phone: string
  email: string
  username?: string
  roleIds: string[]
  isArchived: boolean
  customerIds: number[]
  customerClientIds: number[]
  customerClientId: number
  customers: ICustomer[]
  depotIds: number[]
  newPassword: string
  newPasswordRetry: string
  isNotifyUser: boolean
  isPasswordAutoGenerated: boolean
}

export const EmployeeAddForm = forwardRef<EmployeeAddFormProps>((props, ref) => {
  const [form] = Form.useForm<EmployeeAddFormValues>()
  const [id, setId] = useState<string>("")
  const [isOpen, setIsOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const [isSelectableCustomers, setIsSelectableCustomers] = useState(false)
  const [isSelectableCustomerClients, setIsSelectableCustomerClients] = useState<boolean>(false)
  const [isSelectableDepots, setIsSelectableDepots] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isAutoGenPassword, setIsAutoGenPassword] = useState(false)

  const [filteredListCustomers, setFilteredListCustomers] = useState<ICustomerSelectListItem[]>([])

  const { data: employee } = useEmployeeByIdQuery(id)
  const { createMutation, updateMutation, changePasswordMutation } = useEmployeeMutation()

  const { data: roles } = useEmployeeRolesQuery()
  const { data: customerClients } = useCustomerClientsSelectListQuery()
  const { data: customers } = useCustomersSelectListQuery()
  const { data: depots } = useDepotSelectListQuery()

  useImperativeHandle(ref, () => ({
    open(id?: string, roleName?: string, customerId?: number) {
      onOpenHandler(id, roleName, customerId)
    },
    close() {
      onCloseHandler()
    }
  }))

  useEffect(() => {
    if (employee && isOpen) {
      const response = employee.response

      const isSelectableCustomersMap = response.roles?.map((x) => hasRoleAttribute(x.attributes, attrKeyForCustomers))
      setIsSelectableCustomers(isSelectableCustomersMap?.includes(true) ?? false)

      const isSelectableCustomerClientsMap = response.roles?.map((x) => hasRoleAttribute(x.attributes, attrKeyForCustomerClients))
      setIsSelectableCustomerClients(isSelectableCustomerClientsMap?.includes(true) ?? false)

      const isSelectableDepotsMap = response.roles?.map((x) => hasRoleAttribute(x.attributes, attrKeyForDepots))
      setIsSelectableDepots(isSelectableDepotsMap?.includes(true) ?? false)

      if (isSelectableCustomersMap) {
        filterCustomersSelect(response.customerClients?.length ? response.customerClients[0].id : undefined)
      }

      form.setFields([
        { name: "firstName", value: response.firstName },
        { name: "lastName", value: response.lastName },
        { name: "email", value: response.email },
        { name: "username", value: response.login },
        { name: "phone", value: response.phone },
        { name: "roleIds", value: response.roles?.map((x) => x.id) },
        { name: "customerClientIds", value: response.customerClients.map((cc) => cc.id) },
        { name: "customerClientId", value: response.customerClients?.length ? response.customerClients[0].id : undefined },
        { name: "customerIds", value: response.customers?.map((x) => x.id) },
        { name: "depotIds", value: response.depots?.map((x) => x.id) },
        { name: "isArchived", value: !response.enabled }
      ])
    }
  }, [employee, form, id, isOpen])

  const title = useMemo(() => {
    if (id) {
      return "Редактировать сотрудника"
    }

    return "Добавить сотрудника"
  }, [id])

  const hasRoleAttribute = (attributes: { [key: string]: string[] }, key: string): boolean => {
    if (attributes == null) {
      return false
    }

    return attributes[key] !== undefined && attributes[key][0] === "True"
  }

  const onOpenHandler = (id?: string, roleName?: string, customerId?: number) => {
    setId(id ?? "")
    form.resetFields()
    setIsOpen(true)

    if (customerId && roleName) {
      const roleIds = roles?.response.data.filter((r) => r.name === roleName).map((r) => r.id)
      const customerForCreate = customers?.response.filter((c) => c.value === customerId)
      const customerIds = customerForCreate?.map((c) => c.value)
      const customerClientId = customerForCreate?.at(0)?.customerClientId

      filterCustomersSelect(customerClientId)
      form.setFieldsValue({ roleIds, customerClientId, customerIds })
      onRolesChange()
    }
  }

  const onFinish = async (data: EmployeeAddFormValues) => {
    try {
      setIsProcessing(true)
      const request = {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        phone: data.phone,
        roles: roles?.response.data.filter((x) => data.roleIds?.includes(x.id)),
        customers: isSelectableCustomers
          ? customers?.response
              .filter((x) => data.customerIds?.includes(x.value))
              .map((x) => ({ id: x.value, name: x.label, customerClientId: x.customerClientId, customerClientName: x.customerClientName } as ICustomer))
          : undefined,
        customerClientIds: isSelectableCustomerClients ? data.customerClientIds : undefined,
        depotIds: data.depotIds,
        isArchived: data.isArchived
      }

      if (id) {
        await updateMutation.mutateAsync({
          ...request,
          id
        })

        if (isChangingPassword) {
          await changePasswordMutation.mutateAsync({
            id,
            password: data.newPassword,
            isNotifyUser: data.isNotifyUser,
            isPasswordAutoGenerated: data.isPasswordAutoGenerated
          })
        }
      } else {
        await createMutation.mutateAsync(request)
      }

      form.resetFields()
      onCloseHandler()
    } finally {
      setIsProcessing(false)
    }
  }

  const onCloseHandler = () => {
    setId("")
    form.resetFields()
    setIsOpen(false)
    setIsSelectableCustomerClients(false)
    setIsSelectableCustomers(false)
    setIsSelectableDepots(false)
    setIsChangingPassword(false)
    setIsAutoGenPassword(false)
    setFilteredListCustomers([])
  }

  const onRolesChange = () => {
    const roleIds: string[] = form.getFieldValue("roleIds")
    const selectedRoles = roles?.response.data.filter((x) => roleIds.includes(x.id))
    const rolesWithCustomers = selectedRoles?.filter((x) => hasRoleAttribute(x.attributes, attrKeyForCustomers))
    const rolesWithCustomerClients = selectedRoles?.filter((x) => hasRoleAttribute(x.attributes, attrKeyForCustomerClients))
    const rolesWithDepots = selectedRoles?.filter((x) => hasRoleAttribute(x.attributes, attrKeyForDepots))

    setIsSelectableCustomers(rolesWithCustomers != null && rolesWithCustomers.length > 0)
    if (rolesWithCustomers == null || rolesWithCustomers.length === 0) {
      form.resetFields(["customerIds"])
      form.resetFields(["customerClientId"])
    }

    setIsSelectableCustomerClients(rolesWithCustomerClients != null && rolesWithCustomerClients.length > 0)
    if (rolesWithCustomerClients == null || rolesWithCustomerClients.length === 0) {
      form.resetFields(["customerClientIds"])
    }

    setIsSelectableDepots(rolesWithDepots != null && rolesWithDepots.length > 0)
    if (rolesWithDepots == null || rolesWithDepots.length === 0) {
      form.resetFields(["depotIds"])
    }
  }

  const filterCustomersSelect = (value: number | undefined) => {
    form.resetFields(["customerIds"])

    if (value === undefined) {
      return
    }

    setFilteredListCustomers(customers?.response.filter((c) => c.customerClientId === value) ?? [])
  }

  const onAutoGenPasswordChange = (e: CheckboxChangeEvent) => {
    setIsAutoGenPassword(e.target.checked)
    if (e.target.checked) {
      form.resetFields(["newPassword", "newPasswordRetry"])
    }
  }

  return (
    <Drawer
      title={title}
      width={820}
      onClose={onCloseHandler}
      open={isOpen}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Button type="primary" disabled={isProcessing} onClick={form.submit}>
          Сохранить
        </Button>
      }
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item label="Имя" name="firstName" rules={[{ required: id === "", message: "Пожалуйста введите имя" }]}>
          <Input placeholder="Имя" />
        </Form.Item>
        <Form.Item label="Фамилия" name="lastName" rules={[{ required: id === "", message: "Пожалуйста введите фамилию" }]}>
          <Input placeholder="Фамилия" />
        </Form.Item>
        <Form.Item label="Электронная почта" name="email" rules={[{ type: "email", message: "Пожалуйста введите электронную почту", required: true }]}>
          <Input placeholder="Электронная почта" />
        </Form.Item>
        <Form.Item label="Номер телефона" name="phone" rules={[{ len: PHONE_LENGTH, message: `Номер телефона должен состоять из ${PHONE_LENGTH} цифр` }]}> 
          <NumericInput prefix="+7" type="number" placeholder="Номер телефона" maxLength={PHONE_LENGTH} />
        </Form.Item>
        <Form.Item label="Логин" name="username">
          <Input placeholder="Логин" />
        </Form.Item>
        <Form.Item name="roleIds" label="Роли" rules={[{ required: true }]}> 
          <Select
            mode="multiple"
            placeholder="Роль"
            showSearch
            options={roles?.response.data.map((role) => ({
              value: role.id,
              label: role.name,
              description: role.description
            }))}
            optionRender={(option) => (
              <div>
                <div>{option.data.label}</div>
                {option.data.description && (
                  <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }}>
                    {option.data.description}
                  </div>
                )}
              </div>
            )}
            filterOption={(inputValue, option) => filterSelectOption(inputValue, option?.label || "")}
            onChange={onRolesChange}
          />
        </Form.Item>
        {isSelectableCustomerClients && (
          <Form.Item name="customerClientIds" label="Клиент">
            <Select mode="multiple" placeholder="Клиенты" showSearch options={customerClients?.response} />
          </Form.Item>
        )}
        {isSelectableCustomers && (
          <>
            <Form.Item name="customerClientId" label="Клиент">
              <Select placeholder="Клиент" showSearch options={customerClients?.response} onChange={filterCustomersSelect} />
            </Form.Item>
            <Form.Item name="customerIds" label="Контрагенты" rules={[{ required: isSelectableCustomers }]}> 
              <Select mode="multiple" placeholder="Контрагенты" showSearch options={filteredListCustomers} />
            </Form.Item>
          </>
        )}
        {isSelectableDepots && (
          <Form.Item name="depotIds" label="Нефтебаза" rules={[{ required: isSelectableDepots }]}> 
            <Select
              mode="multiple"
              placeholder="Нефтебаза"
              showSearch
              options={depots?.response.map((depo) => ({ value: depo.id, label: depo.name }))}
            />
          </Form.Item>
        )}
        {id && (
          <Form.Item name="isArchived" valuePropName="checked">
            <Checkbox disabled={!id}>Архивный</Checkbox>
          </Form.Item>
        )}
        {id && (
          <Form.Item name="isChangingPassword" valuePropName="checked">
            <Checkbox onChange={(e) => setIsChangingPassword(e.target.checked)} disabled={!id}>Сменить пароль</Checkbox>
          </Form.Item>
        )}
        {isChangingPassword && (
          <Card>
            <Form.Item name="isPasswordAutoGenerated" valuePropName="checked">
              <Checkbox disabled={!isChangingPassword} onChange={onAutoGenPasswordChange}>Сгенерировать новый пароль автоматически</Checkbox>
            </Form.Item>
            <Form.Item
              label="Новый пароль"
              name="newPassword"
              rules={[
                { required: !isAutoGenPassword, message: "Пожалуйста введите новый пароль" },
                { min: USER_PASSWORD_MINLENGTH, message: `Минимальная длина пароля - ${USER_PASSWORD_MINLENGTH} символов` },
                { pattern: USER_PASSWORD_REGEXP, message: "Пароль должен содержать хотя бы одну заглавную букву, одну цифру" }
              ]}
            >
              <Input.Password disabled={isAutoGenPassword || !isChangingPassword} />
            </Form.Item>
            <Form.Item
              label="Повторите новый пароль"
              name="newPasswordRetry"
              rules={[
                { required: !isAutoGenPassword, message: "Пожалуйста введите новый пароль" },
                { min: USER_PASSWORD_MINLENGTH, message: `Минимальная длина пароля - ${USER_PASSWORD_MINLENGTH} символов` },
                { pattern: USER_PASSWORD_REGEXP, message: "Пароль должен содержать хотя бы одну заглавную букву, одну цифру" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error("Введенные пароли должены совпадать"))
                  }
                })
              ]}
            >
              <Input.Password disabled={isAutoGenPassword || !isChangingPassword} />
            </Form.Item>
            <Form.Item name="isNotifyUser" valuePropName="checked">
              <Checkbox disabled={!employee?.response.email || !isChangingPassword}>Уведомить пользователя по e-mail</Checkbox>
            </Form.Item>
          </Card>
        )}
      </Form>
    </Drawer>
  )
})
